import { ArrowLeft, AlertTriangle, Calendar, Clock, ChevronDown, FileText, Folder, LoaderCircle, SquareArrowOutUpRight, Trash2, User } from "lucide-react"
import { useEffect, useMemo, useRef, useState } from "react"
import { useParams } from "react-router-dom"
import { toast } from "sonner"

import { MarkdownDisplay } from "@/components/displays/MarkdownDisplay"
import { DeleteProgramModal } from "@/components/modals/DeleteProgramModal"
import { DocumentOverlay } from "@/components/overlays/DocumentOverlay"
import { ReportActionDropdown } from "@/features/report/ReportActionDropdown"
import { UpdateReportDescriptionPanel } from "@/features/report/UpdateReportDescriptionPanel"
import { UpdateReportTitleInput } from "@/features/report/UpdateReportTitleInput"
import { Button } from "@ui/button"
import { Separator } from "@ui/separator"
import { useUserCache } from "@hooks/useUserCache"
import { insertMessageInTimeline } from "@/lib/agents"
import { toLocaleDateTimeString } from "@/lib/date"
import { ContentFile } from "@/models/file"
import { Plan } from "@/models/plan"
import { useUserSession } from "@session/UserSession"

import { AsyncExecutionResult } from "@vertesia/client"
import { AgentMessage, AgentMessageType, CollectionItem, ContentObjectItem, WorkflowRunWithDetails, WorkflowExecutionStatus } from "@vertesia/common"

export function ReportSummaryPanel() {
    const { id } = useParams()
    const { client, isLoading } = useUserSession()
    const { getUserDisplayName } = useUserCache(client)

    const [run, setRun] = useState<AsyncExecutionResult | undefined>(undefined)
    const [runDetails, setRunDetails] = useState<WorkflowRunWithDetails | undefined>(undefined)
    const [inError, setInError] = useState<boolean>(false)
    const [messages, setMessages] = useState<AgentMessage[]>([])
    const hasFetchedCompletedReport = useRef(false)

    const [parentProgram, setParentProgram] = useState<CollectionItem | undefined>(undefined)
    const [program, setProgram] = useState<CollectionItem | undefined>(undefined)

    const [configuration, setConfiguration] = useState<CollectionItem | undefined>(undefined)
    const [documents, setDocuments] = useState<ContentObjectItem[]>([])
    const [additionalDocuments, setAdditionalDocuments] = useState<ContentObjectItem[]>([])
    const [template, setTemplate] = useState<ContentObjectItem | undefined>(undefined)
    const [additional, setAdditional] = useState<Record<string, any>>({})

    const [displayDocuments, setDisplayDocuments] = useState<boolean>(false)
    const [displayAdditionalDocuments, setDisplayAdditionalDocuments] = useState<boolean>(false)

    const [fileId, setFileId] = useState<string | undefined>(undefined)
    const [fileText, setFileText] = useState<string | undefined>(undefined)

    const [plans, setPlans] = useState<Array<{ plan: Plan; timestamp: number }>>([])
    const [activePlanIndex, setActivePlanIndex] = useState<number>(0)
    const [workstreamStatusMap, setWorkstreamStatusMap] = useState<Map<number, Map<string, "pending" | "in_progress" | "completed" | "skipped">>>(new Map())

    const getActivePlan = useMemo(() => {
        const currentPlanData = plans[activePlanIndex] || { plan: { plan: [] }, timestamp: 0 }
        const currentWorkstreamStatus = workstreamStatusMap.get(currentPlanData.timestamp) || new Map()
        return {
            plan: currentPlanData.plan,
            workstreamStatus: currentWorkstreamStatus,
        }
    }, [plans, activePlanIndex, workstreamStatusMap])

    const progressPercentage = useMemo(() => {
        return calculateProgress(getActivePlan.plan, getActivePlan.workstreamStatus)
    }, [getActivePlan])

    const navigateToDashboard = () => {
        window.location.pathname = "/report-generator"
    }

    const navigateToParentProgram = () => {
        if (!parentProgram) {
            return
        }

        const properties = (parentProgram as any).properties || {}
        if (!properties.run_id || !properties.workflow_id) {
            toast.error("Parent program does not have a valid run ID or workflow ID.")
            return
        }

        const url = `${window.location.origin}/report-generator/${properties.run_id}__${properties.workflow_id}`
        window.open(url, "_blank")
    }

    const handleProgramNameUpdate = (value: string) => {
        if (!program) {
            return
        }
        program.name = value
    }

    const handleProgramDescriptionUpdate = (value: string) => {
        if (!program) {
            return
        }
        program.description = value
    }

    const handleProgramStateUpdate = (status: string) => {
        if (!program || !client) {
            return
        }

        const properties = (program as any)?.properties
        if (status === properties.status) {
            return
        }

        properties.status = status
        client.store.collections.update(program.id, { properties })
    }

    useEffect(() => {
        if (!client || !run || isLoading) {
            return
        }

        if (!runDetails && !inError) {
            client.workflows.getRunDetails(run.runId, run.workflowId)
                .then((details) => {
                    if (details.status === "FAILED") {
                        setInError(true)
                        handleProgramStateUpdate("error")
                    } else {
                        setRunDetails(details as WorkflowRunWithDetails)
                        streamMessages()
                    }
                })
                .catch(() => {
                    const payload = {
                        limit: 1,
                        match: {
                            "properties.run_id": run.runId,
                            "properties.workflow_id": run.workflowId
                        }
                    }
                    client.store.collections.search(payload)
                        .then((result) => {
                            if (result.length === 1) {
                                updateProgram(result[0])
                            }
                        })
                })
        }

        return () => { setMessages([]) }
    }, [run, client.store.workflows, isLoading])

    const streamMessages = () => {
        if (!client || !run || isLoading) {
            return
        }

        client.store.workflows.streamMessages(run.workflowId, run.runId, (message) => {
            if (message.message) {
                setMessages((prev_messages) => {
                    if (!prev_messages.find((m) => m.timestamp === message.timestamp)) {
                        insertMessageInTimeline(prev_messages, message)
                        return [...prev_messages]
                    } else {
                        return prev_messages
                    }
                })
            }
        })
    }

    const updateProgram = (prog: CollectionItem) => {
        setProgram(prog)

        const properties = (prog as any).properties || {}

        const documentIds: string[] = properties?.documents
        if (documentIds && documentIds.length) {
            const uploadPromises = documentIds.map(document => client.store.objects.retrieve(document))
            Promise.all(uploadPromises).then((inputs) => {
                setDocuments(inputs)
            })
        }

        const additionalIds: string[] = properties?.additional_documents
        if (additionalIds && additionalIds.length) {
            const uploadPromises = additionalIds.map(document => client.store.objects.retrieve(document))
            Promise.all(uploadPromises).then((inputs) => {
                setAdditionalDocuments(inputs)
            })
        }

        const configurationId = properties?.configuration
        if (configurationId) {
            client.store.collections.retrieve(configurationId)
                .then(setConfiguration)
        }

        const templateId = properties?.template
        if (templateId) {
            client.store.objects.retrieve(templateId)
                .then(setTemplate)
        }

        setAdditional(properties?.additional || {})

        const parentId = properties?.parent
        if (parentId) {
            client.store.collections.retrieve(parentId)
                .then(setParentProgram)
        }

        const reportId = properties?.report
        if (reportId) {
            client.store.objects.getObjectText(reportId)
                .then((obj) => {
                    setFileText(obj.text)
                    setFileId(reportId)
                })
        } else {
            const runId = properties?.run_id
            const workflowId = properties?.workflow_id
            setRun({ runId, workflowId })
        }
    }

    const retrieveProgram = (programId: string) => {
        if (!client) {
            return
        }

        client.store.collections.retrieve(programId)
            .then((prog) => {
                updateProgram(prog)
            })
    }

    useEffect(() => {
        messages.forEach((message) => {
            if (message.type === AgentMessageType.PLAN) {
                try {
                    let newPlanDetails: Plan | null = null
                    if (message.details && typeof message.details === "object") {
                        if (message.details.plan && Array.isArray(message.details.plan)) {
                            newPlanDetails = { plan: message.details.plan } as Plan
                        }
                    }

                    if (newPlanDetails) {
                        const timestamp =
                            typeof message.timestamp === "number"
                                ? message.timestamp
                                : new Date(message.timestamp).getTime()

                        const existingPlanIndex = plans.findIndex((p) => p.timestamp === timestamp)

                        if (existingPlanIndex === -1 && newPlanDetails) {
                            const newPlan = {
                                plan: newPlanDetails,
                                timestamp,
                            }

                            setPlans((prev) => {
                                return [newPlan, ...prev]
                            })
                            setActivePlanIndex(0)

                            const newWorkstreamStatus = new Map<string, "pending" | "in_progress" | "completed" | "skipped">()

                            newWorkstreamStatus.set("main", "in_progress")

                            if (Array.isArray(newPlanDetails.plan)) {
                                newPlanDetails.plan.forEach((task) => {
                                    if (task && typeof task === "object" && task.id) {
                                        const taskId = task.id.toString()
                                        newWorkstreamStatus.set(taskId, task.status || "pending")
                                    }
                                })
                            }

                            setWorkstreamStatusMap((prev) => {
                                const newMap = new Map(prev)
                                newMap.set(timestamp, newWorkstreamStatus)
                                return newMap
                            })
                        }
                    }
                } catch (error) {
                    toast.error("Failed to parse plan from message", {
                        description: error instanceof Error ? error.message : "Unknown error occurred",
                        duration: 5000,
                    })
                }
            }
        })
    }, [messages, plans, activePlanIndex])

    useEffect(() => {
        if (!client || !program || progressPercentage !== 100 || fileText || hasFetchedCompletedReport.current) {
            return
        }

        hasFetchedCompletedReport.current = true
        retrieveProgram(program.id)
    }, [client, program, progressPercentage, fileText])

    useEffect(() => {
        if (!id) {
            navigateToDashboard()
            return
        }

        retrieveProgram(id)
    }, [id])

    return (
        <div className="h-full w-full">
            {
                !program
                    ? (
                        <div className="h-full w-full flex items-center justify-center">
                            <div className="flex gap-2 items-center">
                                <LoaderCircle className="size-6 text-gray-500 animate-spin motion-safe:animate-[spin_2s_linear_infinite]" />
                                <p className="text-gray-500 text-2xl">Retriving Report"s information.</p>
                            </div>
                        </div>
                    ) : (
                        <div className="h-full flex flex-col">
                            <div className="flex items-center justify-between gap-2 border-b border-gray-300 p-2 bg-gray-50">
                                <div className="flex items-center gap-2">
                                    <Button onClick={navigateToDashboard}>
                                        <ArrowLeft />
                                        Back to Dashboard
                                    </Button>
                                    <div>
                                        <div className="flex items-center gap-1">
                                            <UpdateReportTitleInput program={program} onUpdate={handleProgramNameUpdate} />
                                        </div>
                                        <div className="flex items-center gap-4 text-sm text-gray-600">
                                            <div className="flex items-center gap-1">
                                                <User className="h-4 w-4" />
                                                <span>{getUserDisplayName(program.created_by, "AI Report Generator")}</span>
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <Calendar className="h-4 w-4" />
                                                <span>Created {toLocaleDateTimeString(new Date(program.created_at))}</span>
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <Clock className="h-4 w-4" />
                                                <span>Updated {toLocaleDateTimeString(new Date(program.updated_at))}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <ReportActionDropdown program={program} download={(!!fileText && !!fileId)} file={fileId} />
                                    {
                                        (program && (program as any)?.properties?.status && (program as any)?.properties?.status !== "processing") &&
                                        <DeleteProgramModal program={program.id} onDelete={navigateToDashboard}>
                                            <Button variant="destructive">
                                                <Trash2 />
                                                Delete
                                            </Button>
                                        </DeleteProgramModal>
                                    }
                                </div>
                            </div>
                            <div className="flex flex-row w-full flex-1 min-h-0">
                                <div className="w-[30%] h-full border-r border-gray-300 bg-gray-50">
                                    <div className="flex flex-col h-full">
                                        <div className="p-2">
                                            <UpdateReportDescriptionPanel program={program} configuration={configuration} onUpdate={handleProgramDescriptionUpdate} />
                                        </div>
                                        <Separator className="bg-gray-300" />
                                        <div className="flex-shrink-0">
                                            {
                                                parentProgram && (
                                                    <>
                                                        <div className="flex flex-col p-2 gap-0.5">
                                                            <h4 className="font-medium text-gray-900 text-sm">Parent</h4>
                                                            <div className="flex items-center gap-2">
                                                                <Folder className="size-4 text-gray-600" />
                                                                <p className="text-gray-600 text-sm truncate" title={parentProgram.name}>{parentProgram.name}</p>
                                                                <Button variant={"ghost"} size="xs" onClick={navigateToParentProgram}>
                                                                    <SquareArrowOutUpRight className="size-4 text-gray-600" />
                                                                </Button>
                                                            </div>
                                                        </div>
                                                        <Separator className="bg-gray-300" />
                                                    </>
                                                )
                                            }
                                            <div className="flex flex-col p-2 gap-0.5">
                                                <h4 className="font-medium text-gray-900 text-sm">Template Used</h4>
                                                {
                                                    template
                                                        ? (
                                                            <DocumentOverlay document={template.id}>
                                                                <div className="flex items-center gap-2 cursor-pointer hover:bg-gray-300 rounded">
                                                                    <FileText className="size-4 text-gray-600" />
                                                                    <p className="text-gray-600 text-sm truncate">{template.name}</p>
                                                                </div>
                                                            </DocumentOverlay>
                                                        ) : (
                                                            <p className="text-gray-600 text-sm">Loading template information</p>
                                                        )
                                                }
                                            </div>
                                        </div>
                                        {
                                            Object.keys(additional).length !== 0 && (
                                                <>
                                                    <Separator className="bg-gray-300" />
                                                    <div className="flex flex-col p-2 gap-2">
                                                        {
                                                            Object.entries(additional).map(([key, value]) => (
                                                                <div key={key} className="flex flex-col">
                                                                    <h4 className="font-medium text-gray-900 text-sm">{key}</h4>
                                                                    <span className="text-sm text-muted-foreground">{value}</span>
                                                                </div>
                                                            ))
                                                        }
                                                    </div>
                                                </>
                                            )
                                        }
                                        <div className="flex flex-col gap-1 flex-1 min-h-0">
                                            <div className="flex flex-col flex-1 overflow-y-auto">
                                                {
                                                    documents.length !== 0 && (
                                                        <>
                                                            <Separator className="bg-gray-300" />
                                                            <div className="flex flex-col p-2 gap-1">
                                                                <div className="flex items-center justify-between">
                                                                    <h4 className="font-medium text-gray-900 text-sm">Source Documents</h4>
                                                                    <Button onClick={() => setDisplayDocuments(!displayDocuments)} size={"xs"} variant={"ghost"}>
                                                                        <ChevronDown className={`transition-transform duration-300 ${displayDocuments ? "rotate-0" : "rotate-180"}`} />
                                                                    </Button>
                                                                </div>
                                                                {
                                                                    displayDocuments && (
                                                                        <div className="flex flex-col gap-1 flex-1 overflow-y-auto">
                                                                            {
                                                                                documents.map((doc) => (
                                                                                    <DocumentOverlay key={doc.id} document={doc.id}>
                                                                                        <div className="flex items-center gap-2 cursor-pointer hover:bg-gray-300 rounded">
                                                                                            <FileText className="size-4 text-gray-600 flex-shrink-0" />
                                                                                            <p className="text-gray-600 text-sm truncate">{doc.name}</p>
                                                                                        </div>
                                                                                    </DocumentOverlay>
                                                                                ))
                                                                            }
                                                                        </div>
                                                                    )
                                                                }
                                                            </div>
                                                        </>
                                                    )
                                                }
                                                {
                                                    additionalDocuments.length !== 0 && (
                                                        <>
                                                            <Separator className="bg-gray-300" />
                                                            <div className="flex flex-col p-2 gap-1">
                                                                <div className="flex items-center justify-between">
                                                                    <h4 className="font-medium text-gray-900 text-sm">Additional Documents</h4>
                                                                    <Button onClick={() => setDisplayAdditionalDocuments(!displayAdditionalDocuments)} size={"xs"} variant={"ghost"}>
                                                                        <ChevronDown className={`transition-transform duration-300 ${displayAdditionalDocuments ? "rotate-0" : "rotate-180"}`} />
                                                                    </Button>
                                                                </div>
                                                                {
                                                                    displayAdditionalDocuments && (
                                                                        <div className="flex flex-col gap-1 flex-1 overflow-y-auto">
                                                                            {
                                                                                additionalDocuments.map((doc) => (
                                                                                    <DocumentOverlay key={doc.id} document={doc.id}>
                                                                                        <div className="flex items-center gap-2 cursor-pointer hover:bg-gray-300 rounded">
                                                                                            <FileText className="size-4 text-gray-600 flex-shrink-0" />
                                                                                            <p className="text-gray-600 text-sm truncate">{doc.name}</p>
                                                                                        </div>
                                                                                    </DocumentOverlay>
                                                                                ))
                                                                            }
                                                                        </div>
                                                                    )
                                                                }
                                                            </div>
                                                        </>
                                                    )
                                                }
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                {
                                    inError ? (
                                        <div className="w-[75%] flex flex-col h-full justify-center items-center gap-2">
                                            <div className="flex flex-col items-center">
                                                <div className="flex flex-row items-center justify-center gap-2 text-4xl text-red-500">
                                                    <AlertTriangle className="size-8" />
                                                    <p>Report Generation encountered an error</p>
                                                </div>
                                                <p className="text-gray-500 text-2xl">Please contact your system administrator for further information</p>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            {
                                                (fileText)
                                                    ? (
                                                        <div className="w-[75%] flex flex-col">
                                                            <div className="flex-1 overflow-auto px-2 pb-2">
                                                                {
                                                                    fileText &&
                                                                    <MarkdownDisplay content={fileText} />
                                                                }
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="w-[75%] flex flex-col h-full justify-center items-center gap-2">
                                                            <div className="flex flex-col items-center gap-2 w-full max-w-xl">
                                                                <div className="flex w-full items-center justify-between gap-2">
                                                                    <div className="text-sm text-blue-600 flex items-center gap-2">
                                                                        <LoaderCircle className="size-6 text-blue-600 animate-spin motion-safe:animate-[spin_2s_linear_infinite]" />
                                                                        Processing documents and generating report...
                                                                    </div>
                                                                    <span className="text-sm text-gray-600">
                                                                        {progressPercentage}%
                                                                    </span>
                                                                </div>
                                                                <div className="w-full bg-gray-200 rounded-full h-2">
                                                                    <div
                                                                        className="bg-blue-600 h-2 rounded-full"
                                                                        style={{ width: `${progressPercentage}%` }}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )
                                            }
                                        </>
                                    )
                                }
                            </div>
                        </div>
                    )
            }
        </div>
    )
}

function decodeWorkflowRun(id: string | undefined) {
    if (!id) {
        return undefined
    }

    const index = id.indexOf("__")
    if (index > 0) {
        const runId = id.substring(0, index)
        const workflowId = id.substring(index + 2)
        if (runId && workflowId) {
            return { runId, workflowId }
        }
    }
    return undefined
}

function extractInformationFromTags(tags: string[], information: string) {
    return tags
        .filter(tag => tag.startsWith(information + ":"))
        .map(tag => tag.substring(information.length + 1).trim())
}

const parseFilesFromMessage = (message: AgentMessage): ContentFile[] => {
    const files: ContentFile[] = []

    if ((message.type === AgentMessageType.ANSWER || message.type === AgentMessageType.UPDATE) && message.message) {
        const messageAsString = typeof message.message === "string"
            ? message.message
            : JSON.stringify(message.message)

        const fileMatches = messageAsString.match(/\[([^\]]+)\]\(store:([a-f\d]{24})\)/gi)

        if (fileMatches) {
            for (const fileMatch of fileMatches) {
                const match = fileMatch.match(/\[(.*?)\]\(store:(.*?)\)/)
                if (match && match[1] && match[2]) {
                    files.push({
                        id: match[2],
                        name: match[1],
                        timestamp: message.timestamp
                    })
                }
            }
        }
    }

    return files
}

function calculateProgress(plan: Plan, workstreamStatus: Map<string, "pending" | "in_progress" | "completed">) {
    const planTasks = plan.plan || []
    const totalTasks = planTasks.length
    let completedTasks = 0

    if (totalTasks > 0) {
        planTasks.forEach((task) => {
            if (task && task.id) {
                const taskId = task.id.toString()
                const taskStatus = workstreamStatus.get(taskId)
                if (taskStatus === "completed") {
                    completedTasks++
                } else if (taskStatus === "in_progress") {
                    completedTasks += 0.5
                }
            }
        })
    }

    return totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0
}